import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
} from 'react';

import { Widget } from '../../utils/enums/embed';
import { CookieConsent } from '../enums';
import {
  InternalMessageType,
  sendMessageToEmbed,
} from '../lib/internal-messages';
import { OpenDialogPayload } from '../types';

interface DialogContext {
  openDialog: (payload: OpenDialogPayload) => void;
  prefetchDialog: (payload: OpenDialogPayload) => void;
  widgetType: Widget;
}

export interface DialogParameters {
  cookieConsent: CookieConsent;
  widgetType: Widget;
  platform?: string;
}

const DialogContext = createContext<DialogContext>({} as DialogContext);

interface Props extends PropsWithChildren {
  params: DialogParameters;
}

export const DialogProvider = ({ children, params }: Props) => {
  const openDialog = useCallback(
    (payload: OpenDialogPayload) => {
      sendMessageToEmbed({
        type: InternalMessageType.OpenDialog,
        payload: {
          ...payload,
          widgetType: params.widgetType,
          cookieConsent: params.cookieConsent,
          ...(params.platform && { platform: params.platform }),
        },
      });
    },
    [params.cookieConsent, params.widgetType, params.platform]
  );

  const prefetchDialog = useCallback(
    (payload: OpenDialogPayload) => {
      sendMessageToEmbed({
        type: InternalMessageType.PrefetchDialog,
        payload: {
          ...payload,
          widgetType: params.widgetType,
          cookieConsent: params.cookieConsent,
          ...(params.platform && { platform: params.platform }),
        },
      });
    },
    [params.cookieConsent, params.widgetType, params.platform]
  );

  return (
    <DialogContext.Provider
      value={{ openDialog, prefetchDialog, widgetType: params.widgetType }}
    >
      {children}
    </DialogContext.Provider>
  );
};

export const useDialogContext = () => useContext(DialogContext);
